import { useSelector } from 'react-redux';
import { AlertsTabIcon, MarkerIcon, SMSIconFilled } from '@rsos/assets/icons';
import AlertsBadge from '@rsos/components/capstone/Queue/AlertsBadge';
import EmergencyCallsBadge from '@rsos/components/capstone/Queue/EmergencyCallsBadge';
import { DISPATCH_REQUESTED } from '@rsos/constants/alertStatuses';
import {
  ACTIVE_ASSAILANT,
  TRAIN_INCIDENT_ALERT_TYPE,
} from '@rsos/constants/alertTypes';
import {
  AGENCY_CHAT,
  ALERTS,
  EMERGENCY_CALLS,
} from '@rsos/constants/incidentTypes';
import { type RootState } from '../../../app';
import AgencyChatBadge from '../../InteragencyChat/AgencyChatBadge/AgencyChatBadge';
import styles from './Tabs.module.scss';
import {
  getIconClass,
  getLabelClass,
  getTabClass,
  type NotifyByAlertType,
} from './helpers';

interface TabsProps {
  activeQueueTab: string;
  hasAccessToInteragencyChat: boolean;
  hasAlertsAccess: boolean;
  isEmergencyCallsEnabled: boolean;
  onQueueTabClick: (tab: string) => void;
}

const Tabs = ({
  activeQueueTab,
  hasAccessToInteragencyChat,
  hasAlertsAccess,
  isEmergencyCallsEnabled,
  onQueueTabClick,
}: TabsProps) => {
  const activeAssailantAlerts = useSelector(
    (state: RootState) => state.incidents.activeAssailantAlerts,
  );
  const notifyActiveAssailant =
    !!Object.values(activeAssailantAlerts).find(
      a => a.status.name === DISPATCH_REQUESTED,
    ) && activeQueueTab !== ALERTS;

  const { trainAlerts } = useSelector((state: RootState) => state.incidents);
  const notifyTrainIncident =
    !!Object.values(trainAlerts).find(
      a => a.status.name === DISPATCH_REQUESTED,
    ) && activeQueueTab !== ALERTS;

  const notifyByAlertType: NotifyByAlertType = notifyActiveAssailant
    ? ACTIVE_ASSAILANT
    : notifyTrainIncident
      ? TRAIN_INCIDENT_ALERT_TYPE
      : null;

  const visibleTabsCount = [
    isEmergencyCallsEnabled,
    hasAlertsAccess,
    hasAccessToInteragencyChat,
  ].filter(Boolean).length;

  const tabSpread =
    visibleTabsCount === 1
      ? 'single'
      : visibleTabsCount === 2
        ? 'double'
        : 'multiple';

  return (
    <>
      <ul className={styles.tabsContainer} data-spread={tabSpread}>
        {isEmergencyCallsEnabled && (
          <li
            className={getTabClass(activeQueueTab === EMERGENCY_CALLS, null)}
            data-name="911calls-tab"
            id={EMERGENCY_CALLS}
            onClick={() => onQueueTabClick(EMERGENCY_CALLS)}
          >
            <span className={getIconClass(EMERGENCY_CALLS, null)}>
              <MarkerIcon width={15} height={14} />
            </span>
            <span className={getLabelClass(EMERGENCY_CALLS, null)}>911</span>
            <EmergencyCallsBadge />
          </li>
        )}
        {hasAlertsAccess && (
          <li
            className={getTabClass(
              activeQueueTab === ALERTS,
              notifyByAlertType,
            )}
            data-name="Alerts-tab"
            id={ALERTS}
            onClick={() => onQueueTabClick(ALERTS)}
          >
            <span className={getIconClass(ALERTS, notifyByAlertType)}>
              <AlertsTabIcon
                width={9}
                height={14}
                viewboxWidth={9}
                viewboxHeight={14}
              />
            </span>
            <span className={getLabelClass(ALERTS, notifyByAlertType)}>
              Alerts
            </span>
            <AlertsBadge notifyByAlertType={notifyByAlertType} />
          </li>
        )}
        {hasAccessToInteragencyChat && (
          <li
            className={getTabClass(activeQueueTab === AGENCY_CHAT, null)}
            data-name="agency-chat-tab"
            id={AGENCY_CHAT}
            onClick={() => onQueueTabClick(AGENCY_CHAT)}
          >
            <span className={getIconClass(AGENCY_CHAT, null)}>
              <SMSIconFilled width={14} height={14} />
            </span>
            <span className={getLabelClass(AGENCY_CHAT, null)}>
              Agency Chat
            </span>
            <AgencyChatBadge />
          </li>
        )}
      </ul>
      <div className={styles.queueBorderTabs} />
    </>
  );
};

export default Tabs;
